if (!customElements.get('speakon-home-polish')) {
  customElements.define('speakon-home-polish', class extends HTMLElement {
    connectedCallback() {
      this.stop?.();
      const reduced = matchMedia('(prefers-reduced-motion: reduce)');
      const controller = new AbortController();
      const options = { signal: controller.signal };
      const copies = [...this.querySelectorAll('[data-copy]')];
      const sentences = copies.map(copy => Array.from(copy.textContent));
      const cursors = [...this.querySelectorAll('[data-cursor]')];
      const keys = [...this.querySelectorAll('.speakon-polish__key')];
      const shape = this.querySelector('[data-mic-shape]');
      const icon = this.querySelector('[data-mic-icon]');
      const wave = this.querySelector('[data-wave]');
      const bars = [...wave.children];
      const glow = this.querySelector('[data-glow]');
      const spinner = this.querySelector('[data-spinner]');
      const envelope = [.12, .18, .27, .42, .62, .84, 1, .72, .52, .34, .23, .16, .1];
      const clamp = value => Math.min(1, Math.max(0, value));
      const smooth = value => { const t = clamp(value); return t * t * (3 - 2 * t); };
      let elapsed = 0, last = 0, frame = 0, visible = false;
      let counts = [-1, -1], activeKey = -1;

      // Timing and waveform adapted from the supplied dist/motions/voice-speed.html.
      const render = (ms, final = false) => {
        const t = final ? 6000 : Math.min(ms % 6750, 6000);
        const typingTime = Math.max(0, t - 250);
        const started = t >= 250;
        const nextCounts = [
          final ? sentences[0].length : Math.min(sentences[0].length, started ? Math.floor(typingTime / 1000 * 3.75) + 1 : 0),
          Math.min(sentences[1].length, started ? Math.floor(clamp(typingTime / 5250) * sentences[1].length) + 1 : 0)
        ];
        nextCounts.forEach((count, i) => {
          if (counts[i] !== count) copies[i].textContent = sentences[i].slice(0, count).join('');
        });
        counts = nextCounts;
        const blink = Math.floor(t / 208) % 2 === 0 ? '1' : '0';
        cursors.forEach((cursor, i) => { cursor.style.opacity = final || t >= 6000 || counts[i] >= sentences[i].length ? '0' : blink; });
        const keyIndex = !final && started && typingTime % (1000 / 3.75) < 78 && counts[0] < sentences[0].length
          ? ((sentences[0][Math.max(0, counts[0] - 1)]?.codePointAt(0) || 0) + counts[0] * 7) % keys.length : -1;
        if (keyIndex !== activeKey) {
          keys[activeKey]?.classList.remove('is-active');
          keys[keyIndex]?.classList.add('is-active');
          activeKey = keyIndex;
        }

        const listening = t >= 250 && t < 4917;
        const thinking = t >= 4917 && t < 5833;
        const phase = !started ? 'idle' : listening ? 'listening' : thinking ? 'thinking' : 'complete';
        if (this.dataset.phase !== phase) this.dataset.phase = phase;
        const enter = smooth((t - 250) / 250);
        const process = smooth((t - 4917) / 250);
        const finish = smooth((t - 5292) / 541);
        const waveOpacity = listening ? enter : thinking ? 1 - process : 0;
        const micOpacity = !started ? 1 : listening ? 1 - enter : thinking ? finish : 1;
        const width = listening ? 105 + 75 * enter : thinking ? 180 - 75 * process : 105;
        const height = listening ? 105 - 33 * enter : thinking ? 72 + 33 * process : 105;
        shape.style.clipPath = `inset(${(105 - height) / 2}px ${(180 - width) / 2}px round ${height / 2}px)`;
        shape.style.transform = `scale(${1 - Math.sin(clamp((t - 250) / 167) * Math.PI) * .06})`;
        icon.style.opacity = String(micOpacity);
        wave.style.opacity = String(waveOpacity);
        wave.style.transform = `scale(${.88 + enter * .12})`;
        glow.style.opacity = thinking ? String(.5 * process * (1 - finish)) : '0';
        spinner.style.opacity = thinking ? String(process * (1 - finish)) : '0';
        spinner.style.transform = `translate(-50%, -50%) rotate(${t * .432}deg) scale(${.76 + process * .24})`;
        bars.forEach((bar, i) => {
          const time = t / 1000 * 24;
          const primary = Math.sin(time * (.28 + i * .006) + i * 1.21);
          const secondary = Math.sin(time * .43 + i * .67) * .34;
          const height = 3 + waveOpacity * (2 + envelope[i] * 27 * (.45 + Math.abs(primary + secondary) * .55));
          bar.style.transform = `scaleY(${height / 38})`;
        });
      };
      const tick = now => {
        elapsed += now - last;
        last = now;
        render(elapsed);
        frame = requestAnimationFrame(tick);
      };
      const sync = () => {
        cancelAnimationFrame(frame);
        this.dataset.playing = String(visible && !document.hidden && !reduced.matches);
        if (reduced.matches) render(0, true);
        else if (this.dataset.playing === 'true') { last = performance.now(); frame = requestAnimationFrame(tick); }
      };
      const observer = new IntersectionObserver(entries => {
        visible = entries[0].isIntersecting;
        sync();
      }, { threshold: 0 });
      observer.observe(this.querySelector('[data-comparison]'));
      document.addEventListener('visibilitychange', sync, options);
      reduced.addEventListener('change', sync, options);
      render(0, true);
      sync();
      this.stop = () => {
        cancelAnimationFrame(frame);
        observer.disconnect();
        controller.abort();
        keys[activeKey]?.classList.remove('is-active');
        this.dataset.playing = 'false';
        // Restore the source text before a Theme Editor detach/reconnect.
        copies.forEach((copy, i) => { copy.textContent = sentences[i].join(''); });
      };
    }

    disconnectedCallback() { this.stop?.(); }
  });
}
